import { defineStore } from "pinia";

import { getCompanyId } from "@/utils/userUtils";
import {
  orderStatusBorderColor as resolveOrderStatusBorderColor,
  orderStatusColor as resolveOrderStatusColor,
  orderStatusLabel,
  orderStatusSelectOptions,
} from "@/utils/orderStatusUtils";
import {
  orderPaymentFormLabel,
  orderPaymentFormOptions,
} from "@/utils/orderPaymentFormUtils";
import { formatRussianCount } from "@/utils/pluralizationUtils";

import ordersApi from "@/services/api/ordersApi";
import referenceItemsApi from "@/services/api/referenceItemsApi";
import { useNotificationStore } from "@/stores/notificationStore";

export const useOrderStore = defineStore("order", {
  state: () => ({
    orders: [],
    orderTypes: [],

    companyId: getCompanyId(),

    searchQuery: "",

    pagination: {
      page: 1,
      pages: 1,
      series: [],
      next: null,
      prev: null,
      count: 0,
      limit: 40,
    },

    dataIsLoading: false,
    isFinishingCreation: false,
    isBatchRecalculating: false,

    statusOptions: orderStatusSelectOptions(),

    paymentStatusOptions: [
      {
        value: "not_invoiced",
        label: "Не выставлен",
        color: "#6b7280",
      },
      { value: "invoiced", label: "Выставлен", color: "#4f46e5" },
      {
        value: "partially_paid",
        label: "Частично оплачен",
        color: "#4f46e5",
      },
      { value: "paid", label: "Полностью оплачен", color: "#16a34a" },
      {
        value: "overdue",
        label: "Просрочен",
        color: "var(--color-danger)",
      },
      {
        value: "payment_canceled",
        label: "Отменен",
        color: "var(--color-danger)",
      },
    ],

    paymentFormOptions: orderPaymentFormOptions(),
  }),

  getters: {
    // Геттеры для статусов заказа
    orderStatusLabels: (state) =>
      Object.fromEntries(
        state.statusOptions.map((opt) => [opt.value, opt.label])
      ),

    paymentStatusLabels: (state) =>
      Object.fromEntries(
        state.paymentStatusOptions.map((opt) => [
          opt.value,
          opt.label,
        ])
      ),

    paymentFormLabels: (state) =>
      Object.fromEntries(
        state.paymentFormOptions.map((opt) => [opt.value, opt.label])
      ),

    // Опции для BaseStatusSelect: статус заказа с цветом
    orderStatusSelectOptions() {
      return this.statusOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
        color: opt.color,
      }));
    },

    // Опции для BaseStatusSelect: статус оплаты с цветом
    paymentStatusSelectOptions() {
      return this.paymentStatusOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
        color: opt.color,
      }));
    },

    // Опции для BaseStatusSelect: типы заказов из справочника
    orderTypeSelectOptions() {
      return this.orderTypes.map((type) => ({
        value: String(type.id),
        label: type.name,
        color: type.color,
      }));
    },
  },

  actions: {
    async recalculatePlans(orderIds, calculatorPresetId) {
      this.isBatchRecalculating = true;
      try {
        const response = await ordersApi.recalculate(this.companyId, {
          order_ids: orderIds,
          calculator_preset_id: calculatorPresetId,
        });
        const result = response.data.result;
        const countLabel = formatRussianCount(result.enqueued_count, {
          one: "заказа",
          few: "заказов",
          many: "заказов",
        });
        useNotificationStore().showSuccess(
          `Пересчёт ПЛАНа запущен для ${countLabel}`
        );
        return result;
      } finally {
        this.isBatchRecalculating = false;
      }
    },

    // Заказ сохранён на ранних шагах. Уведомление создаём только при явном
    // завершении мастера; необязательный рейс сервер сохраняет в той же транзакции.
    async finishCreation(orderId, data = {}) {
      if (this.isFinishingCreation) return false;

      this.isFinishingCreation = true;
      try {
        await ordersApi.finishCreation(getCompanyId(), orderId, data);
        return true;
      } finally {
        this.isFinishingCreation = false;
      }
    },

    // Методы форматирования статусов
    formatOrderStatus(status) {
      return orderStatusLabel(status);
    },

    formatPaymentStatus(status) {
      return this.paymentStatusLabels[status] || status;
    },

    formatPaymentForm(paymentForm) {
      if (!paymentForm) return "—";
      return orderPaymentFormLabel(paymentForm) || "Другое";
    },

    orderStatusColor(status) {
      return resolveOrderStatusColor(status);
    },

    paymentStatusColor(status) {
      return (
        this.paymentStatusOptions.find(
          (option) => option.value === status
        )?.color || "#6b7280"
      );
    },

    orderStatusBorderColor(status) {
      return resolveOrderStatusBorderColor(status);
    },

    // Обновляет статус заказа без перезагрузки всей формы
    async updateStatus(orderId, status) {
      await ordersApi.update(this.companyId, orderId, { status });
    },

    // Обновляет статус оплаты заказа без перезагрузки всей формы
    async updatePaymentStatus(orderId, paymentStatus) {
      await ordersApi.update(this.companyId, orderId, { payment_status: paymentStatus });
    },

    // Обновляет тип заказа без перезагрузки всей формы
    async updateOrderType(orderId, orderTypeId) {
      await ordersApi.update(this.companyId, orderId, { order_type_id: orderTypeId });
    },

    // Загружает доступные типы заказов из справочника; повторный вызов игнорируется если уже загружены
    async fetchOrderTypes() {
      if (this.orderTypes.length > 0) return;
      const response = await referenceItemsApi.index(this.companyId, {
        "q[target_model]": "Order",
        "q[target_field]": "order_type",
        "q[enabled]": "true",
      });
      this.orderTypes = response.data.result || [];
    },

    async fetchOrders(params = {}) {
      this.searchQuery = params.search || "";
      return await this.getOrdersRequest(1);
    },

    async loadMoreOrders() {
      if (this.dataIsLoading || !this.pagination.next) {
        return;
      }

      const nextPage = this.pagination.page + 1;
      return await this.getOrdersRequest(nextPage);
    },

    async getOrdersRequest(page) {
      const pageNumber = parseInt(page) || 1;

      try {
        this.dataIsLoading = true;

        const params = {
          page: pageNumber,
          "q[enabled]": "true",
        };

        if (this.searchQuery) {
          params["q[search]"] = this.searchQuery;
        }

        const response = await ordersApi.index(
          this.companyId,
          params
        );

        if (pageNumber === 1) {
          this.orders = response.data.result;
        } else {
          this.orders = [...this.orders, ...response.data.result];
        }

        const meta = response.data.pagination_meta;
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
        };
      } catch (error) {
        console.error("Ошибка при загрузке заказов:", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    clearOrders() {
      this.orders = [];
      this.searchQuery = "";
      this.pagination = {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      };
    },
  },
});
